import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { PORTAL_API } from "@/lib/portalApi";
import { useStartupPortalAuth } from "@/contexts/StartupPortalAuthContext";

// Module-level guard — magic-link tokens are single-use; we MUST never POST
// the same token twice even if the Verify component is remounted by React
// (StrictMode, hot-reload, route-replay, …). A Map remembers each token's
// in-flight promise so a concurrent remount returns the same answer.
const _inFlight = new Map();

export default function PortalVerify() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeLogin } = useStartupPortalAuth();
  const [state, setState] = useState({ status: "checking", message: "" });
  const token = params.get("token");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ status: "error", message: t("portal.verify.missingToken") });
        return;
      }
      try {
        let promise = _inFlight.get(token);
        if (!promise) {
          promise = axios.post(
            `${PORTAL_API}/magic-link/verify`,
            { token },
            { withCredentials: true },
          );
          _inFlight.set(token, promise);
        }
        const { data } = await promise;
        if (cancelled) return;
        await completeLogin(data);
        navigate("/portal/startup/dashboard", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message:
            err?.response?.status === 401
              ? t("portal.verify.linkInvalid")
              : t("portal.verify.errorGeneric"),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, completeLogin, navigate, t]);

  return (
    <div className="min-h-screen bg-porcelain flex items-center justify-center p-5" data-testid="portal-verify">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md bg-white border border-sandBeige rounded-[2rem] shadow-soft p-10 text-center"
      >
        {state.status === "checking" ? (
          <>
            <Loader2 className="mx-auto animate-spin text-raspberry" size={28} />
            <p className="font-serif text-2xl text-cocoa mt-5" data-testid="verify-loading">
              {t("portal.verify.loading")}
            </p>
            <p className="text-warmGrey text-sm mt-2">{t("portal.verify.loadingHint")}</p>
          </>
        ) : (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-roseMilk text-coral flex items-center justify-center">
              <AlertCircle size={22} />
            </div>
            <p className="font-serif text-2xl text-cocoa mt-5" data-testid="verify-error-title">
              {t("portal.verify.errorTitle")}
            </p>
            <p className="text-warmGrey text-sm mt-3">{state.message}</p>
            <Link
              to="/portal/startup/login"
              className="mt-7 inline-flex items-center gap-2 bg-raspberry hover:bg-coral text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
              data-testid="verify-retry"
            >
              {t("portal.verify.retry")} <ArrowRight size={14} />
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
